import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { INR } from "@/lib/constants";
import { X, Plus, Trash2, Sparkles, GripVertical, ChevronDown, ChevronUp } from "lucide-react";
import { PhoneInput } from "@/components/PhoneInput";

const SLOT_OPTIONS = ["Morning", "Afternoon", "Evening", "Night"];
const EXTRA_DEFS = [
  { key: "travel", name: "Outstation Travel Charges" },
  { key: "stay", name: "Stay (Hotels)" },
  { key: "extra", name: "Extra Charges", editable: true },
];
const DISCOUNT_OPTS = [
  { v: "none", label: "No discount" },
  { v: "flat", label: "Flat ₹" },
  { v: "percent", label: "Percent %" },
];

function computeCalc(q) {
  const subtotal =
    (q.events || []).reduce((s, e) => s + (Number(e.price) || 0) + (e.addons || []).reduce((x, a) => x + (Number(a.price) || 0), 0), 0) +
    (q.addons || []).reduce((s, a) => s + (Number(a.price) || 0), 0) +
    (q.extras || []).reduce((s, a) => s + (Number(a.price) || 0), 0);
  let discount = 0;
  if (q.discount_type === "flat") discount = Math.min(Number(q.discount_value) || 0, subtotal);
  else if (q.discount_type === "percent") discount = Math.round((subtotal * (Number(q.discount_value) || 0)) / 100);
  const total = Math.max(subtotal - discount, 0);
  const advance =
    q.advance_type === "percent"
      ? Math.round((total * (Number(q.advance_value) || 0)) / 100)
      : Math.min(Math.round(Number(q.advance_value) || 0), total);
  return { subtotal, discount, total, advance, balance: total - advance };
}

const emptyEvent = () => ({ _key: Math.random().toString(36).slice(2), event_type: "", service_id: "", service_name: "", date: "", slots: [], ready_by: "", location: "", price: 0, addons: [] });
const withKeys = (events) => (events || []).map((e) => (e._key ? e : { ...e, _key: Math.random().toString(36).slice(2) }));
const initExtras = (existing) => EXTRA_DEFS.map((d) => {
  const found = (existing || []).find((e) => e.key === d.key) || (existing || []).find((e) => e.name === d.name);
  return { key: d.key, name: d.editable && found?.name ? found.name : d.name, price: found ? found.price : 0, editable: !!d.editable };
});

export default function QuoteBuilder({ open, onClose, onSaved, settings, references, quote, seed }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [expanded, setExpanded] = useState(0);

  useEffect(() => {
    if (!open) return;
    setExpanded(0);
    if (quote) {
      setForm({ ...quote, events: quote.events?.length ? withKeys(quote.events) : [emptyEvent()], addons: quote.addons || [], reference_id: quote.reference_id || "", hidden_sections: quote.hidden_sections || [], extras: initExtras(quote.extras) });
    } else {
      setForm({
        mode: "direct",
        client_name: seed?.client_name || "",
        client_phone: seed?.client_phone || "",
        valid_until: "",
        events: seed?.events?.length ? withKeys(seed.events.map((e) => ({ ...emptyEvent(), ...e }))) : [emptyEvent()],
        addons: [],
        discount_type: "none",
        discount_value: 0,
        advance_type: settings?.advance_default_type || "percent",
        advance_value: settings?.advance_default_value || 25,
        reference_id: "",
        hidden_sections: [],
        extras: initExtras(),
        status: "draft",
      });
    }
  }, [open, quote, seed, settings]);

  const calc = useMemo(() => (form ? computeCalc(form) : {}), [form]);
  if (!open || !form) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setEvent = (i, patch) => set({ events: form.events.map((e, idx) => (idx === i ? { ...e, ...patch } : e)) });
  const toggleSlot = (i, slot) => {
    const cur = form.events[i].slots || [];
    setEvent(i, { slots: cur.includes(slot) ? cur.filter((s) => s !== slot) : [...cur, slot] });
  };
  const priceKey = form.mode === "partner" ? "partner_price" : "direct_price";
  const serviceById = (id) => (settings?.services || []).find((s) => String(s.id) === String(id));
  const servicePrice = (id, m) => { const s = serviceById(id); return s ? (m === "partner" ? s.partner_price : s.direct_price) : 0; };
  const setMode = (m) => setForm((f) => ({ ...f, mode: m, events: f.events.map((e) => (e.service_id ? { ...e, price: servicePrice(e.service_id, m) } : e)) }));
  const setEventService = (i, id) => { const s = serviceById(id); setEvent(i, { service_id: id, service_name: s ? s.name : "", price: s ? (form.mode === "partner" ? s.partner_price : s.direct_price) : 0 }); };
  const addEventAddon = (i, item) => setEvent(i, { addons: [...(form.events[i].addons || []), item] });
  const setEventAddon = (i, j, patch) => setEvent(i, { addons: (form.events[i].addons || []).map((a, idx) => (idx === j ? { ...a, ...patch } : a)) });
  const removeEventAddon = (i, j) => setEvent(i, { addons: (form.events[i].addons || []).filter((_, idx) => idx !== j) });
  const moveEvent = (from, to) => {
    if (from == null || to == null || from === to) return;
    setForm((f) => { const arr = [...f.events]; const [m] = arr.splice(from, 1); arr.splice(to, 0, m); return { ...f, events: arr }; });
  };

  const sectionLabel = (key) => {
    if (key === "header") return "Header (top bar)";
    if (key === "footer") return "Footer";
    if (key.startsWith("gallery:")) { const g = (settings?.galleries || []).find((x) => x.id === key.split(":")[1]); return `Gallery — ${g?.header || "Untitled"}`; }
    const L = { galleries: "Portfolio Galleries", why_choose_us: "Why Choose Us?", instagram: "Instagram", pricing: "Pricing & Events", faqs: "FAQ", reviews: "Reviews", terms: "Terms & Inclusions" };
    return L[key] || key;
  };
  const availableSections = ["header", ...(settings?.section_order || []).filter((k) => k !== "about"), "footer"];
  const isHidden = (key) => (form.hidden_sections || []).includes(key);
  const toggleHidden = (key) => set({ hidden_sections: isHidden(key) ? form.hidden_sections.filter((k) => k !== key) : [...(form.hidden_sections || []), key] });
  const setExtra = (i, price) => set({ extras: form.extras.map((e, idx) => (idx === i ? { ...e, price } : e)) });
  const setExtraName = (i, name) => set({ extras: form.extras.map((e, idx) => (idx === i ? { ...e, name } : e)) });

  const save = async () => {
    if (!form.client_name.trim()) { toast.error("Client name is required"); return; }
    setSaving(true);
    const payload = {
      ...form,
      events: form.events.filter((e) => e.event_type || e.service_id).map(({ _key, ...e }) => ({
        ...e, price: Number(e.price) || 0,
        addons: (e.addons || []).map((a) => ({ name: a.name, price: Number(a.price) || 0 })),
      })),
      addons: [],
      discount_value: Number(form.discount_value) || 0,
      advance_value: Number(form.advance_value) || 0,
      reference_id: form.mode === "partner" ? (form.reference_id || null) : null,
      hidden_sections: form.hidden_sections || [],
      extras: (form.extras || []).filter((e) => Number(e.price) > 0).map((e) => ({ key: e.key, name: (e.name || "").trim() || "Extra Charges", price: Number(e.price) || 0 })),
    };
    try {
      const res = quote ? await api.put(`/quotes/${quote.id}`, payload) : await api.post("/quotes", payload);
      toast.success(quote ? "Quote updated" : "Quote created");
      onSaved(res.data);
      onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full px-3 py-2 bg-white border border-[#EAE7E0] text-sm text-[#2A2421] focus:border-[#C88270] focus:outline-none";
  const labelCls = "block text-[10px] uppercase tracking-[0.2em] text-[#5C5652] mb-1.5";

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="quote-builder">
      <div className="absolute inset-0 bg-[#2A2421]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative h-full w-full max-w-2xl bg-[#FAF9F6] shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
        <div className="sticky top-0 z-10 bg-[#FAF9F6]/95 backdrop-blur border-b border-[#EAE7E0] px-6 py-4 flex items-center justify-between">
          <div className="font-serif text-2xl text-[#2A2421]">{quote ? "Edit Quotation" : "New Quotation"}</div>
          <button onClick={onClose} data-testid="builder-close" className="p-2 text-[#5C5652] hover:text-[#2A2421]"><X size={20} /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Mode */}
          <div className="flex gap-2" data-testid="builder-mode">
            {["direct", "partner"].map((m) => (
              <button key={m} onClick={() => setMode(m)} data-testid={`builder-mode-${m}`}
                className={`flex-1 px-4 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${form.mode === m ? "bg-[#2A2421] text-white border-[#2A2421]" : "bg-white border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421]"}`}>
                {m === "direct" ? "Direct Client" : "Partner Client"}
              </button>
            ))}
          </div>

          {form.mode === "partner" && (
            <div data-testid="builder-reference-wrap">
              <label className={labelCls}>Reference partner (optional)</label>
              <select value={form.reference_id || ""} onChange={(e) => set({ reference_id: e.target.value })} data-testid="builder-reference" className={inputCls}>
                <option value="">No reference</option>
                {(references || []).map((r) => <option key={r.id} value={r.id}>{r.company ? `${r.name} · ${r.company}` : r.name}</option>)}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Client name *</label>
              <input value={form.client_name} onChange={(e) => set({ client_name: e.target.value })} data-testid="builder-client-name" className={inputCls} placeholder="Priya & Arjun" />
            </div>
            <div>
              <label className={labelCls}>Client phone (optional)</label>
              <PhoneInput value={form.client_phone || ""} onChange={(v) => set({ client_phone: v })} testId="builder-client-phone" placeholder="90000 00000" />
            </div>
            <div>
              <label className={labelCls}>Valid until (optional)</label>
              <input type="date" value={form.valid_until || ""} onChange={(e) => set({ valid_until: e.target.value })} data-testid="builder-valid-until" className={inputCls} />
            </div>
          </div>

          {/* Events */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium text-[#2A2421]">Events</div>
              <button onClick={() => { set({ events: [...form.events, emptyEvent()] }); setExpanded(form.events.length); }} data-testid="builder-add-event"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#C88270] hover:text-[#2A2421]"><Plus size={14} /> Add event</button>
            </div>
            <div className="space-y-3">
              {form.events.map((ev, i) => {
                const open = expanded === i;
                const summary = [ev.event_type || `Event ${i + 1}`, ev.date || null].filter(Boolean).join(" · ");
                return (
                <div key={ev._key} draggable onDragStart={() => setDragIndex(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => { moveEvent(dragIndex, i); setDragIndex(null); }}
                  className={`bg-white border ${dragIndex === i ? "border-[#C88270] opacity-60" : "border-[#EAE7E0]"}`} data-testid={`builder-event-${i}`}>
                  <div className="flex items-center gap-2 p-3">
                    <span className="cursor-grab text-[#B8B2AA] hover:text-[#5C5652] shrink-0" title="Drag to reorder" data-testid={`builder-event-drag-${i}`}><GripVertical size={16} /></span>
                    <button type="button" onClick={() => setExpanded(open ? -1 : i)} data-testid={`builder-event-toggle-${i}`} className="flex-1 min-w-0 flex items-center justify-between text-left gap-2">
                      <span className="text-sm font-medium text-[#2A2421] truncate">{summary}{Number(ev.price) > 0 ? ` · ${INR(Number(ev.price))}` : ""}</span>
                      {open ? <ChevronUp size={16} className="text-[#5C5652] shrink-0" /> : <ChevronDown size={16} className="text-[#5C5652] shrink-0" />}
                    </button>
                    {form.events.length > 1 && (
                      <button onClick={() => { set({ events: form.events.filter((_, idx) => idx !== i) }); setExpanded(Math.max(0, i - 1)); }} data-testid={`builder-remove-event-${i}`} className="p-2 text-[#5C5652] hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
                    )}
                  </div>
                  {open && (
                  <div className="px-4 pb-4 pt-3 space-y-3 border-t border-[#EAE7E0]">
                    <div>
                      <label className={labelCls}>Event type</label>
                      <select value={ev.event_type} onChange={(e) => setEvent(i, { event_type: e.target.value })} data-testid={`builder-event-type-${i}`} className={inputCls}>
                        <option value="">Select event type…</option>
                        {(settings?.event_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><label className={labelCls}>Date</label><input type="date" value={ev.date || ""} onChange={(e) => setEvent(i, { date: e.target.value })} className={inputCls} /></div>
                      <div><label className={labelCls}>Ready by</label><input value={ev.ready_by || ""} onChange={(e) => setEvent(i, { ready_by: e.target.value })} placeholder="06:00 AM" className={inputCls} /></div>
                    </div>
                    <div>
                      <label className={labelCls}>Slots to block</label>
                      <div className="flex flex-wrap gap-2">
                        {SLOT_OPTIONS.map((s) => (
                          <button key={s} onClick={() => toggleSlot(i, s)} data-testid={`builder-slot-${i}-${s}`}
                            className={`px-3 py-1.5 text-xs border transition-colors ${(ev.slots || []).includes(s) ? "bg-[#C88270] text-white border-[#C88270]" : "bg-white border-[#EAE7E0] text-[#5C5652] hover:border-[#C88270]"}`}>{s}</button>
                        ))}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-1">
                        <label className={labelCls}>Service</label>
                        <select value={ev.service_id || ""} onChange={(e) => setEventService(i, e.target.value)} data-testid={`builder-event-service-${i}`} className={inputCls}>
                          <option value="">Select…</option>
                          {(settings?.services || []).map((sv) => <option key={sv.id} value={sv.id}>{sv.name}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Price ₹ (editable)</label>
                        <input type="number" value={ev.price} onChange={(e) => setEvent(i, { price: e.target.value })} data-testid={`builder-event-price-${i}`} className={inputCls} />
                      </div>
                      <div><label className={labelCls}>Location</label><input value={ev.location || ""} onChange={(e) => setEvent(i, { location: e.target.value })} placeholder="Venue" className={inputCls} /></div>
                    </div>

                    {/* Per-event add-ons */}
                    <div className="pt-2 border-t border-[#EAE7E0]">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#5C5652]">Optional add-ons</span>
                        <button onClick={() => addEventAddon(i, { name: "", price: 0 })} data-testid={`builder-add-eventaddon-${i}`} className="text-xs text-[#C88270] hover:text-[#2A2421] inline-flex items-center gap-1"><Plus size={12} /> Add</button>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(settings?.services || []).map((svc) => (
                          <button key={svc.id} onClick={() => addEventAddon(i, { name: svc.name, price: svc[priceKey] })} data-testid={`builder-quick-svc-${i}-${svc.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-[#FAF9F6] border border-[#EAE7E0] text-[#5C5652] hover:border-[#C88270] hover:text-[#2A2421] transition-colors">
                            <Plus size={11} /> {svc.name} · {INR(svc[priceKey])}
                          </button>
                        ))}
                      </div>
                      {(ev.addons || []).map((a, j) => (
                        <div key={j} className="flex items-center gap-2 mb-1.5" data-testid={`builder-eventaddon-${i}-${j}`}>
                          <input value={a.name} onChange={(e) => setEventAddon(i, j, { name: e.target.value })} placeholder="Add-on name" className={inputCls} />
                          <input type="number" value={a.price} onChange={(e) => setEventAddon(i, j, { price: e.target.value })} className={`${inputCls} w-28`} />
                          <button onClick={() => removeEventAddon(i, j)} className="p-2 text-[#5C5652] hover:text-red-600"><Trash2 size={15} /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>

          {/* Notes / Special requests */}
          <div data-testid="builder-notes-block">
            <label className={labelCls}>Notes / Special requests <span className="text-[10px] uppercase tracking-[0.15em] text-[#5C5652]">· optional</span></label>
            <textarea value={form.notes || ""} onChange={(e) => set({ notes: e.target.value })} rows={3} data-testid="builder-notes"
              placeholder="Any specific requirement or request from the client (e.g. preferred look, allergies, timing, travel details)…" className={inputCls} />
          </div>

          {/* Additional charges (optional) */}
          <div data-testid="builder-extras">
            <div className="text-sm font-medium text-[#2A2421] mb-3">Additional Charges <span className="text-[10px] uppercase tracking-[0.15em] text-[#5C5652]">· optional</span></div>
            <div className="space-y-2">
              {(form.extras || []).map((ex, i) => (
                <div key={ex.key} className="grid grid-cols-12 gap-2 items-center" data-testid={`builder-extra-${i}`}>
                  {ex.editable ? (
                    <input value={ex.name} onChange={(e) => setExtraName(i, e.target.value)} data-testid={`builder-extra-name-${i}`} placeholder="Charge label" className={`${inputCls} col-span-8`} />
                  ) : (
                    <span className="col-span-8 text-sm text-[#2A2421]">{ex.name}</span>
                  )}
                  <input type="number" value={ex.price} onChange={(e) => setExtra(i, e.target.value)} data-testid={`builder-extra-price-${i}`} placeholder="0" className={`${inputCls} col-span-4`} />
                </div>
              ))}
            </div>
          </div>

          {/* Discount + advance */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Discount</label>
              <div className="flex gap-2">
                <select value={form.discount_type} onChange={(e) => set({ discount_type: e.target.value })} data-testid="builder-discount-type" className={inputCls}>
                  {DISCOUNT_OPTS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
                {form.discount_type !== "none" && (
                  <input type="number" value={form.discount_value} onChange={(e) => set({ discount_value: e.target.value })} data-testid="builder-discount-value" className={`${inputCls} w-28`} />
                )}
              </div>
            </div>
            <div>
              <label className={labelCls}>Advance</label>
              <div className="flex gap-2">
                <select value={form.advance_type} onChange={(e) => set({ advance_type: e.target.value })} data-testid="builder-advance-type" className={inputCls}>
                  <option value="percent">Percent %</option>
                  <option value="fixed">Fixed ₹</option>
                </select>
                <input type="number" value={form.advance_value} onChange={(e) => set({ advance_value: e.target.value })} data-testid="builder-advance-value" className={`${inputCls} w-28`} />
              </div>
            </div>
          </div>

          {/* Section visibility */}
          <div data-testid="builder-visibility">
            <label className={labelCls}>Show / hide in this quote</label>
            <div className="flex flex-wrap gap-2">
              {availableSections.map((key) => (
                <button key={key} onClick={() => toggleHidden(key)} data-testid={`builder-visibility-${key}`}
                  className={`px-3 py-1.5 text-xs border transition-colors ${isHidden(key) ? "bg-white border-[#EAE7E0] text-[#B8B2AA] line-through" : "bg-[#C88270] text-white border-[#C88270]"}`}>
                  {sectionLabel(key)}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[#5C5652] mt-1.5">Coral = shown · struck-out = hidden on the PDF & public link.</p>
          </div>

          {/* Live totals */}
          <div className="bg-white border border-[#EAE7E0] p-4 space-y-1.5 text-sm" data-testid="builder-totals">
            <Row label="Subtotal" value={INR(calc.subtotal)} />
            {calc.discount > 0 && <Row label="Discount" value={`- ${INR(calc.discount)}`} />}
            <Row label="Total" value={INR(calc.total)} bold coral />
            {calc.advance > 0 && <Row label="Advance to block date" value={INR(calc.advance)} />}
            {calc.advance > 0 && <Row label="Balance (event day)" value={INR(calc.balance)} />}
          </div>
        </div>

        <div className="sticky bottom-0 bg-[#FAF9F6]/95 backdrop-blur border-t border-[#EAE7E0] px-6 py-4 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-[#5C5652] hover:text-[#2A2421]">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="builder-save"
            className="inline-flex items-center gap-2 px-6 py-2.5 text-xs uppercase tracking-[0.2em] bg-[#2A2421] text-white hover:bg-[#C88270] transition-colors disabled:opacity-50">
            <Sparkles size={14} /> {saving ? "Saving…" : quote ? "Update Quote" : "Create Quote"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold, coral }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[#5C5652]">{label}</span>
      <span className={`${bold ? "font-serif text-lg" : ""} ${coral ? "text-[#C88270]" : "text-[#2A2421]"}`}>{value}</span>
    </div>
  );
}

